import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArcadeShell } from '../arcade/ArcadeShell';
import { TurnShell } from '../board/TurnShell';
import { SoloShell } from '../solo/SoloShell';
import { findGame } from '../games/catalog';
import { LOADERS } from '../games/loaders';
import type { CatalogEntry } from '../games/catalog';
import type { GameModule } from '../games/module';
import { sound } from '../lib/audio';
import { AdSlot } from '../ads/AdSlot';
import { NotFound } from './NotFound';
import { setGameMeta } from '../lib/seo';
import { GameGuide } from '../components/GameGuide';
import { reloadForNewVersion } from '../lib/staleReload';
import { PlayNext } from '../components/PlayNext';
import { markPlayed } from '../lib/library';
import { GameActions } from '../components/LikeButton';

export function GamePage() {
  const { id = '' } = useParams();
  const entry = findGame(id);
  const [mod, setMod] = useState<{ id: string; module: GameModule } | null>(null);
  const [failed, setFailed] = useState(false);
  // Every game opens on a loading screen; the game starts when the player presses Play.
  const [startedId, setStartedId] = useState<string | null>(null);

  useEffect(() => {
    const load = LOADERS[id];
    if (!load) return;
    let alive = true;
    setFailed(false);
    load()
      .then((m) => alive && setMod({ id, module: m.default }))
      .catch(() => {
        // Usually a tab opened before the latest deploy asking for game files that no longer exist.
        if (alive && !reloadForNewVersion()) setFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [id]);

  if (!entry || !LOADERS[id]) return <NotFound />;
  if (failed) {
    return (
      <main className="page page--narrow" id="main">
        <div className="card card--center" role="alert">
          <h1 className="card__title">Couldn’t load {entry.name}</h1>
          <p className="muted">Check your connection, then try again.</p>
          <button type="button" className="btn" onClick={() => location.reload()}>
            Try again
          </button>
        </div>
      </main>
    );
  }
  const loaded = !!mod && mod.id === id;
  if (startedId !== id) {
    return withPlayNext(
      entry,
      <Launch
        key={id}
        entry={entry}
        loaded={loaded}
        onPlay={() => {
          sound.unlock();
          markPlayed(id);
          setStartedId(id);
        }}
      />,
    );
  }
  if (!mod || mod.id !== id) {
    return (
      <main className="page page--narrow" id="main">
        <div className="card card--center" role="status">
          <span className="spinner" aria-hidden="true" />
          <p>Loading {entry.name}…</p>
        </div>
      </main>
    );
  }
  const m = mod.module;
  if (m.kind === 'arcade') return withPlayNext(entry, <ArcadeShell key={id} entry={entry} def={m.def} />);
  if (m.kind === 'turn') return withPlayNext(entry, <TurnShell key={id} entry={entry} def={m.def} />);
  return withPlayNext(entry, <SoloShell key={id} entry={entry} def={m.def} />);
}

/** The game (launch screen or the game itself) with the Play next column beside it on wide screens. */
function withPlayNext(entry: CatalogEntry, game: ReactNode) {
  return (
    <div className="game-layout">
      <div className="game-layout__main">{game}</div>
      <PlayNext entry={entry} />
    </div>
  );
}

const MODE_LABEL = { solo: 'Singleplayer', keyboard: '2 players · one keyboard', touch: '2 players · pass and play' } as const;

/** Splash shown while the game's code downloads: cover art, a loading bar, then a big Play button. */
function Launch({ entry, loaded, onPlay }: { entry: CatalogEntry; loaded: boolean; onPlay: () => void }) {
  const [pct, setPct] = useState(0);
  // Short minimum so the bar is seen to fill rather than flashing.
  const [minDone, setMinDone] = useState(false);
  const ready = loaded && minDone;
  const playRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setGameMeta(entry);
  }, [entry.name]);
  useEffect(() => {
    const t = setTimeout(() => setMinDone(true), 700);
    return () => clearTimeout(t);
  }, []);
  // The download size isn't known, so the bar eases toward 90% and jumps to 100% when the game is ready.
  useEffect(() => {
    if (ready) {
      setPct(100);
      return;
    }
    const iv = setInterval(() => setPct((p) => p + (90 - p) * 0.08), 60);
    return () => clearInterval(iv);
  }, [ready]);
  useEffect(() => {
    if (ready) playRef.current?.focus({ preventScroll: true });
  }, [ready]);

  return (
    <main className="page game-page launch" id="main">
      <div className="launch__stage">
        {entry.thumb && <div className="launch__bg" style={{ backgroundImage: `url(${entry.thumb})` }} aria-hidden="true" />}
        <div className="launch__inner">
          <div className="launch__cover" aria-hidden="true">
            {entry.thumb ? <img src={entry.thumb} alt="" width={512} height={512} /> : <entry.Icon />}
          </div>
          <h1 className="launch__title">{entry.name}</h1>
          <p className="launch__tagline">{entry.tagline}</p>
          <p className="launch__modes">
            {entry.categoryLabel} · {entry.modes.map((m) => MODE_LABEL[m]).join(' · ')}
          </p>
          <div className="launch__action">
            {ready ? (
              <button ref={playRef} type="button" className="launch__play" onClick={onPlay}>
                <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
                  <path d="M8 5.5v13a1 1 0 0 0 1.5.86l10.4-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5z" fill="currentColor" />
                </svg>
                Play
              </button>
            ) : (
              <div className="launch__loading" role="progressbar" aria-label={`Loading ${entry.name}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)}>
                <div className="launch__bar">
                  <div className="launch__fill" style={{ width: `${pct}%` }} />
                </div>
                <span className="launch__pct">Loading… {Math.round(pct)}%</span>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="game-bar launch__bar-under">
        <span className="game-bar__name">{entry.name}</span>
        <GameActions gameId={entry.id} path={entry.path} />
      </div>
      <AdSlot placement="launch" />
      <Link to="/" className="btn btn--ghost btn--small launch__back">
        ← All games
      </Link>
      <GameGuide entry={entry} />
    </main>
  );
}
